import { get, orderBy } from 'lodash'

import type { Filter } from '@/components/UI/Filter/utils/types'
import type { Sort } from '@/components/UI/Sort/utils/types'
import { SortDirection } from '@/components/UI/Sort/utils/enums'
import { PivotTableGroup } from './enums'
import type { PivotTableCaptions, PivotTableData, PivotTableFields, PivotTablePercentFields, PivotTableValueRendererKey, PivotTableValueRenderers } from './types'

import { ALL_OPENED_SENTINEL, CLOSED_UNDER_ALL_PREFIX, COL_GROUP_DEPTH_KEY, PATH_DELIMITER, PIVOT_TABLE_LAZY_COUNT } from './const'
import { excludePercentFieldNames, getExportableValueFields, getPercentFieldColumns, getPercentSelectCondition } from './percentFields'
import { UNKNOWN_VALUE } from '@/const'

import { escapeSqlString } from '@/helpers/common'

import { expandDynamicField, getAnalyticsBaseQuerySelect, getAnalyticsDuckDbFieldName, getAnalyticsTotalQuerySelect } from '@/store/analytics/utils/queries'

export const renderValue = (
  value: any,
  col: string,
  renderers?: PivotTableValueRenderers,
  key?: PivotTableValueRendererKey,
  revealed?: boolean,
) => {
  if (renderers?.[col]) {
    return renderers?.[col](value, col, key, undefined, revealed)
  }
  return value
}

export const buildColsIndexMap = (colsList: string[]) => new Map(colsList.map((key, index) => [key, index]))

export const sortColumns = (column: PivotTableData, colsList: string[], colsIndexMap?: Map<string, number>) => {
  const indexMap = colsIndexMap ?? buildColsIndexMap(colsList)
  const entries = Object.entries(column).filter(([key]) => key !== COL_GROUP_DEPTH_KEY)
  return orderBy(entries, ([key]) => indexMap.get(key) ?? -1).map(item => item[1])
}

// Like Object.values/entries but skips COL_GROUP_DEPTH_KEY
export const getColumnPathValues = (column: PivotTableData) =>
  Object.entries(column).filter(([key]) => key !== COL_GROUP_DEPTH_KEY).map(([, value]) => value)

// Sums a collapsed column-group's hidden children client-side. The backend doesn't compute column-group subtotals; all leaf values are in `fieldValue`. `remainingDepth` is levels left below; 0 means node is a leaf.
export const sumColumnSubtree = (node: Record<string, any> | undefined, remainingDepth: number, valueField: string): number => {
  if (!node || typeof node !== 'object') return 0
  if (remainingDepth <= 0) {
    const value = node[valueField]
    return typeof value === 'number' ? value : 0
  }
  let sum = 0
  for (const key of Object.keys(node)) {
    sum += sumColumnSubtree(node[key], remainingDepth - 1, valueField)
  }
  return sum
}

export const getValuesByColumns = (item: PivotTableData, columns: PivotTableData[], colsList: string[], valuesList: string[]) => {
  const result: { value: number, path: string[], valueField: string }[] = []
  const colsField = colsList[0]
  let fieldValue: Record<string, any> = {}
  try {
    fieldValue = JSON.parse(item[colsField])
  } catch {}
  const colsIndexMap = buildColsIndexMap(colsList)
  for (const column of columns || []) {
    const colsPath = sortColumns(column, colsList, colsIndexMap)
    const groupDepth = column[COL_GROUP_DEPTH_KEY] as number | undefined
    result.push(...valuesList.map(valueField => {
      const path = [...colsPath, valueField]
      const value = groupDepth === undefined
        ? (get(fieldValue, path, 0) as number)
        : sumColumnSubtree(get(fieldValue, colsPath), colsList.length - colsPath.length, valueField)
      return {
        value,
        path,
        valueField,
      }
    }))
  }
  return result
}

// Collapses hidden leaf columns into a single group entry (with `COL_GROUP_DEPTH_KEY`), showing only collapsed dimension values. Data remains in `fieldValue`, summed as needed by `getValuesByColumns`.
export const applyColumnCollapse = (columns: PivotTableData[], colsList: string[], collapsedPaths: string[]): PivotTableData[] => {
  if (!collapsedPaths.length || colsList.length < 2) return columns
  const collapsedSet = new Set(collapsedPaths)
  const seenGroupPaths = new Set<string>()
  const result: PivotTableData[] = []
  for (const column of columns) {
    let collapsedAtDepth = -1
    let groupPath = ''
    for (let depth = 0; depth < colsList.length - 1; depth++) {
      groupPath = depth === 0 ? String(column[colsList[depth]]) : `${groupPath}${PATH_DELIMITER}${column[colsList[depth]]}`
      if (collapsedSet.has(groupPath)) {
        collapsedAtDepth = depth
        break
      }
    }
    if (collapsedAtDepth === -1) {
      result.push(column)
      continue
    }
    if (seenGroupPaths.has(groupPath)) continue
    seenGroupPaths.add(groupPath)
    const groupEntry: PivotTableData = { [COL_GROUP_DEPTH_KEY]: collapsedAtDepth }
    for (let depth = 0; depth <= collapsedAtDepth; depth++) {
      groupEntry[colsList[depth]] = column[colsList[depth]]
    }
    result.push(groupEntry)
  }
  return result
}

// A path is "covered" by a `CLOSED_UNDER_ALL_PREFIX` entry when it's that exact path or one of
// its descendants (the next segment starts right after the delimiter) — a raw string prefix would
// also match an unrelated sibling whose path happens to start the same way (e.g. closing
// "Checking" must not also match "Checking2").
const isPathOrDescendant = (path: string, ancestorPath: string) =>
  path === ancestorPath || path.startsWith(`${ancestorPath}${PATH_DELIMITER}`)

export const getIsOpened = (path: string | undefined, openedRows: { value: string[] } | undefined) => {
  if (!path) return false
  const list = openedRows?.value
  if (!list) return false
  if (!list.includes(ALL_OPENED_SENTINEL)) return list.includes(path)
  // Under "expand all", every path is open unless it (or an ancestor) was explicitly closed
  // back — see `removeOpenedRow`.
  return !list.some(item =>
    item.startsWith(CLOSED_UNDER_ALL_PREFIX) &&
    isPathOrDescendant(path, item.slice(CLOSED_UNDER_ALL_PREFIX.length)),
  )
}

export const addOpenedRow = (path: string | undefined, openedRows: { value: string[] } | undefined) => {
  if (!path || !openedRows) return
  if (openedRows.value.includes(ALL_OPENED_SENTINEL)) {
    // Re-opening a path closed under "expand all" — drop just its own exception. `all` already
    // covers everything else, including any children of this path left in their own state.
    openedRows.value = openedRows.value.filter(item => item !== `${CLOSED_UNDER_ALL_PREFIX}${path}`)
    return
  }
  openedRows.value.push(path)
}

export const removeOpenedRow = (path: string | undefined, openedRows: { value: string[] } | undefined) => {
  if (!path || !openedRows) return
  if (openedRows.value.includes(ALL_OPENED_SENTINEL)) {
    // Closing one row under "expand all" can't just drop the sentinel — that would close
    // everything else too — so it's recorded as an exception instead (see `getIsOpened`).
    openedRows.value = [...openedRows.value, `${CLOSED_UNDER_ALL_PREFIX}${path}`]
    return
  }
  openedRows.value = openedRows.value.filter(item => !item.startsWith(path))
}

export const renderCaption = (value: string, captions?: PivotTableCaptions) => {
  return captions?.[value] || value
}

export const getPivotColumns = (
  tableName: string,
  inputFields: PivotTableFields,
  filter?: Filter,
  tagNames?: string[],
  staticFilterConditions?: Record<string, any>,
  includeCategoryAccount = false,
) => {
  // pick enabled fields and preserve user order by index
  const fields = orderBy(Object.entries(inputFields).filter(([, field]) => field.enabled), item => item[1].index)
  // split enabled fields into column groupings
  const cols = fields?.filter(([, field]) => field.group === PivotTableGroup.COLS).map(([key]) => key)
  const listOfCols = cols.map(getAnalyticsDuckDbFieldName(tagNames))
  const posibleValues = Object.entries(inputFields).filter(([, field]) => field.group === PivotTableGroup.VALUES).map(([key]) => key)

  // short-circuit when there is nothing to pivot or aggregate
  if (cols.length === 0) return

  const unknownValue = escapeSqlString(UNKNOWN_VALUE)

  const result = []
  result.push(...getAnalyticsTotalQuerySelect(tableName, staticFilterConditions, posibleValues, false, includeCategoryAccount))
  result.push(',')
  result.push(...getAnalyticsBaseQuerySelect(filter, tagNames, posibleValues, [], false, includeCategoryAccount))

  // build final select
  result.push('SELECT DISTINCT')
  const selectConditions: string[] = []
  const orderByConditions: string[] = []
  for (const col of listOfCols) {
    selectConditions.push(`\tCOALESCE(CAST(${col} AS VARCHAR), '${unknownValue}') AS ${col}`)
    orderByConditions.push(col)
  }
  result.push(selectConditions.join(',\n'))
  result.push('FROM base')
  result.push(`ORDER BY ${orderByConditions.join(', ')};`)

  // return the full query string
  return result.join('\n')
}

export type PivotedDataParentPathEntry = {
  field: string
  value: string
}

export type GetPivotedDataOptions = {
  tableName: string
  inputFields: PivotTableFields
  filter?: Filter
  sortRows?: Sort[]
  tagNames?: string[]
  staticFilterConditions?: Record<string, string>
  percentFields?: PivotTablePercentFields
  totalsInTheEnd?: boolean
  offset?: number
  includeCategoryAccount?: boolean
  limit?: number
  // ancestor row field/value pairs, for fetching a node's children; omit/empty for root
  parentPath?: PivotedDataParentPathEntry[]
}

export const getPivotedData = (options: GetPivotedDataOptions) => {
  const {
    tableName,
    inputFields,
    filter,
    sortRows,
    tagNames,
    staticFilterConditions,
    percentFields,
    totalsInTheEnd = false,
    offset = 0,
    includeCategoryAccount = false,
    limit = PIVOT_TABLE_LAZY_COUNT,
    parentPath,
  } = options
  // pick enabled fields and preserve user order by index
  const fieldsEntries = Object.entries(inputFields)
  const existsPercentFields = percentFields?.filter(([fieldName]) => fieldsEntries.some(([key, field]) => field.enabled && key === fieldName))
  const percentFieldColumns = getPercentFieldColumns(existsPercentFields)
  const fields = orderBy(fieldsEntries.filter(([key, field]) => field.enabled || percentFieldColumns.includes(key)), item => item[1].index)
  // Partition enabled fields into row groups, limited to subtree depth (parentPath + 1)
  const maxRowDepth = (parentPath?.length ?? 0) + 1
  const rows = fields?.filter(([, field]) => field.group === PivotTableGroup.ROWS).map(([key]) => key).slice(0, maxRowDepth)
  const listOfRows = rows.map(getAnalyticsDuckDbFieldName(tagNames))
  // split enabled fields into column groupings
  const cols = fields?.filter(([, field]) => field.group === PivotTableGroup.COLS).map(([key]) => key)
  const listOfCols = cols.map(getAnalyticsDuckDbFieldName(tagNames))
  // collect all value fields for aggregation
  const values = excludePercentFieldNames(
    fields.filter(([, field]) => field.group === PivotTableGroup.VALUES).map(([key]) => key),
    percentFields,
  )
  const posibleValues = excludePercentFieldNames(
    fieldsEntries.filter(([, field]) => field.group === PivotTableGroup.VALUES).map(([key]) => key),
    percentFields,
  )

  // short-circuit when there is nothing to pivot or aggregate
  if (values.length === 0 && rows.length === 0) return

  // normalize dynamic level names to safe identifiers
  const prepareName = (name: string) => name.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()

  const MAP_NAME = 'result_map'
  const ALL_VALUE = '\'__ALL__\''
  const unknownValue = escapeSqlString(UNKNOWN_VALUE)

  // restricts query to a node's subtree; value already normalized from previous fetch
  const ancestorPinConditions = (parentPath || []).map(({ field, value }) => {
    const column = getAnalyticsDuckDbFieldName(tagNames)(field)
    return `CAST(${column} AS VARCHAR) = '${escapeSqlString(value)}'`
  })

  const excludeFields: string[] = []

  const result = []
  // prepare aggregation expressions for values
  const valuesSelectConditions: string[] = []
  for (const value of values) {
    valuesSelectConditions.push(`\t\tSUM(${value}) AS ${value}`)
  }
  // prepare aggregation expressions for percent fields
  const percentSelectConditions: string[] = []
  if (existsPercentFields?.length) {
    for (const percentField of existsPercentFields) {
      percentSelectConditions.push(`\t\t${getPercentSelectCondition(percentField)}`)
    }
  }
  result.push(...getAnalyticsTotalQuerySelect(tableName, staticFilterConditions, posibleValues, false, includeCategoryAccount))
  result.push(',')
  result.push(...getAnalyticsBaseQuerySelect(filter, tagNames, posibleValues, [], false, includeCategoryAccount))
  ;(listOfRows.length > 0 || listOfCols.length > 0) && result.push(',')
  // short-circuit to a simple select when there are no columns and no rows
  if (listOfCols.length === 0 && listOfRows.length === 0) {
    result.push('SELECT')
    result.push([...valuesSelectConditions, ...percentSelectConditions].join(',\n'))
    result.push('FROM base')
    return result.join('\n')
  }
  // build row grouping sets when rows exist
  if (listOfRows.length > 0) {
    // expand rows with an ALL bucket for subtotal rollups
    result.push('base_all AS (')
    result.push('\tSELECT')
    const baseAllSelectConditions: string[] = []
    const baseAllSelectConditions1: string[] = []
    for (const [index, key] of listOfRows.entries()) {
      const keys = expandDynamicField(key)
      for (const key of keys) {
        // Row dimensions must be cast to VARCHAR here
        baseAllSelectConditions.push(`\t\tCAST(${key} AS VARCHAR) AS ${key}`)
        baseAllSelectConditions1.push(`\t\t${index === 0 ? `${ALL_VALUE} AS ${key}` : `CAST(${key} AS VARCHAR) AS ${key}`}`)
      }
    }
    for (const col of listOfCols) {
      const cols = expandDynamicField(col)
      for (const col of cols) {
        baseAllSelectConditions.push(`\t\t${col}`)
        baseAllSelectConditions1.push(`\t\t${col}`)
      }
    }
    for (const value of values) {
      baseAllSelectConditions.push(`\t\t${value}`)
      baseAllSelectConditions1.push(`\t\t${value}`)
    }
    result.push(baseAllSelectConditions.join(',\n'))
    result.push('\tFROM base')
    ancestorPinConditions.length && result.push(`\tWHERE ${ancestorPinConditions.join(' AND ')}`)
    result.push('\tUNION ALL')
    result.push('\tSELECT')
    result.push(baseAllSelectConditions1.join(',\n'))
    result.push('\tFROM base')
    ancestorPinConditions.length && result.push(`\tWHERE ${ancestorPinConditions.join(' AND ')}`)
    result.push('),')
    // group by row grouping sets to compute row-level totals
    result.push('groupped_by_sets AS (')
    result.push('\tSELECT')
    const byFieldsSelectConditions: string[] = []
    for (const row of listOfRows) {
      const rows = expandDynamicField(row)
      for (const [index, row] of rows.entries()) {
        byFieldsSelectConditions.push(`\t\t${index === 0 ? row : `MAX(${row}) AS ${row}`}`)
      }
    }
    byFieldsSelectConditions.push(...valuesSelectConditions, ...percentSelectConditions)
    result.push(byFieldsSelectConditions.join(',\n'))
    result.push('\tFROM base_all')
    result.push('\tGROUP BY GROUPING SETS')
    result.push('\t(')
    const groupBySet: string[] = []
    const groupBySets = listOfRows.reduce((acc, row, index) => {
      acc.push([...(acc[index - 1] || []), row])
      return acc
    }, [] as string[][])
    groupBySet.push(...groupBySets.map(row => `\t\t(${row.join(', ')})`))
    result.push(groupBySet.join(',\n'))
    result.push('\t)')
    result.push('),')
  }
  // initialize SELECT/WHERE parts for the final projection
  const selectConditions: string[] = []
  const whereConditions: string[] = []
  // build row selectors and ordering, handling the ALL bucket
  for (const [index, row] of listOfRows.entries()) {
    if (index === 0) {
      const internalCondition = [`t.${row} = ${ALL_VALUE}`]
      if (listOfRows.length > 1) {
        internalCondition.push(`t.${listOfRows.at(1)} IS NULL`)
      }
      selectConditions.push(`\tCASE WHEN ${internalCondition.join(' AND ')} THEN NULL ELSE t.${row} END AS ${row}`)
    } else {
      selectConditions.push(`\tt.${row} AS ${row}`)
    }
  }
  // select value columns when rows are present
  if (listOfRows.length > 0) {
    for (const value of values) {
      selectConditions.push(`\t${listOfRows.length > 0 ? `t.${value}` : `SUM(t.${value}) AS ${value}`}`)
    }
  }
  // filter out the artificial ALL row when needed
  if (listOfRows.length > 1) {
    whereConditions.push(`t.${listOfRows.at(0)} <> ${ALL_VALUE}`)
    whereConditions.push(`t.${listOfRows.at(1)} IS NULL`)
  }
  // prepare sort conditions for rows
  const orderByConditions: string[] = []
  if (listOfRows.length > 0) {
    orderByConditions.push(`CASE WHEN t.${listOfRows.at(0)} = ${ALL_VALUE} THEN 0 ELSE 1 END`)
    orderByConditions.push(`CASE WHEN t.${listOfRows.at(0)} IS NULL THEN 0 ELSE 1 END`)
  }
  // prepare order by fields map
  const orderByFieldsMap = new Map<string, string[][]>()
  for (const [index, row] of listOfRows.entries()) {
    orderByFieldsMap.set(row.replace(/"/g, ''), [
      [`t.${row}`, '', index === 0 ? '' : `NULLS ${totalsInTheEnd ? 'LAST' : 'FIRST'}`]
    ])
  }
  const orderBySelectConditions: string[] = []
  // if sortRows is not provided, use values to sort
  const sortRowsList = !!sortRows?.length ? sortRows : values.map(field => ({
    field,
    sortDirection: SortDirection.DESC,
  }))
  // prepare order by conditions
  for (const sortRow of sortRowsList || []) {
    const sortDirection = sortRow.sortDirection.toUpperCase()
    let conditionString = ''
    let keyString = ''
    try {
      const column = JSON.parse(sortRow.field) as [string, string][]
      const keys = column.map(([key]) => key)
      const pathKeys = keys.slice(0, -1)
      // exclude unaccessible columns
      if (!listOfCols.every(key => pathKeys.includes(key.replace(/"/g, '')))) {
        continue
      }
      keyString = column.map(([, value]) => value).map(value => prepareName(`${value}`)).join('_')
      const valueKey = keys.at(-1)
      // prepare order by aggregation fields
      const orderSelectItemConditions: string[] = []
      orderSelectItemConditions.push('COALESCE((')
      orderSelectItemConditions.push(`\t\t\tSELECT MAX(u${keys.length - 2}.entry.value.${valueKey})`)
      orderSelectItemConditions.push(`\t\t\tFROM UNNEST(map_entries(ty.${MAP_NAME})) AS u0(entry)`)
      const orderWhereConditions: string[] = []
      // if there more than 2 columns we need make deep join
      if (keys.length > 2) {
        for (const [index] of keys.slice(1, -1).entries()) {
          orderSelectItemConditions.push(`\t\t\tCROSS JOIN UNNEST(map_entries(u${index}.entry.value)) AS u${index + 1}(entry)`)
        }
      }
      // prepare order by where conditions
      for (const [index] of pathKeys.entries()) {
        orderWhereConditions.push(`u${index}.entry.key = '${column[index][1]}'`)
      }
      orderSelectItemConditions.push(`\t\t\tWHERE ${orderWhereConditions.join(' AND ')}`)
      orderSelectItemConditions.push('\t\t), 0)')
      conditionString = orderSelectItemConditions.join('\n')
    } catch {
      const column = sortRow.field
      if (!values.includes(column)) {
        const field = orderByFieldsMap.get(column)
        if (field) {
          orderByFieldsMap.set(column, field.map(item => {
            item[1] = sortDirection
            return item
          }))
        } else {
          orderByFieldsMap.set(column, [
            [`t.${column}`, sortDirection]
          ])
        }
        continue
      }
      conditionString = `t.${column}`
      keyString = column
    }
    for (let k = 0; k < listOfRows.length; k++) {
      const row = listOfRows.at(k)!
      const clearedRow = row.replace(/"/g, '')
      const sortKey = `${prepareName(clearedRow)}_${keyString}_sort`
      const orderSelectItemConditions: string[] = []
      orderSelectItemConditions.push('\tMAX(')
      const nextRow = listOfRows.at(k + 1)
      if (listOfRows.length === 1) {
        orderSelectItemConditions.push(`\t\t${conditionString}`)
      } else if (k === 0) {
        orderSelectItemConditions.push(`\t\tCASE WHEN t.${nextRow} IS NULL THEN ${conditionString} END`)
      } else if (nextRow) {
        const whenConditions = listOfRows.slice(k).map((row, i) => `t.${row} ${i === 0 ? 'IS NOT NULL' : 'IS NULL'}`).join(' AND ')
        orderSelectItemConditions.push(`\t\tCASE WHEN ${whenConditions} THEN ${conditionString} END`)
      } else {
        orderSelectItemConditions.push(`\t\tCASE WHEN t.${row} IS NOT NULL THEN ${conditionString} END`)
      }
      const byFields = listOfRows.slice(0, k + 1).map(row => `t.${row}`).join(', ')
      orderSelectItemConditions.push(`\t) OVER (PARTITION BY ${byFields}) AS ${sortKey}`)
      orderBySelectConditions.push(orderSelectItemConditions.join('\n'))
      let field = orderByFieldsMap.get(clearedRow) || []
      if (k !== 0 && !field.find(item => item[0] === `(t.${row} IS NOT NULL)`)) {
        field.unshift([`(t.${row} IS NOT NULL)`, totalsInTheEnd ? 'DESC' : 'ASC'])
      }
      if (field.length > 0) {
        const result = field.slice(0, -1)
        const lastItem = field.at(-1)
        result.push([sortKey, sortDirection])
        lastItem && result.push(lastItem)
        field = result
      }
      orderByFieldsMap.set(clearedRow, field)
      excludeFields.push(sortKey)
    }
  }

  const orderByFields = Array.from(orderByFieldsMap.values())
    .map(value => value.map(item => item.filter(item => !!item).join(' ')).join(',\n\t'))
  orderByConditions.push(...orderByFields)
  // build nested row/column levels for column maps
  const rowLvls: string[] = []
  let lastScope = 'base'
  for (let i = listOfRows.length - 1; i >=0; i--) {
    const row = listOfRows[i]
    const rowLvl = `${prepareName(row)}_lvl`
    // build row-level aggregation scope
    result.push(`${rowLvl} AS (`)
    result.push('\tSELECT')
    const byRowSelectConditions: string[] = []
    const byRowGroupBy: string[] = []
    const byRowAllGroupBy: string[] = []
    for (const key of listOfRows.slice(0, i + 1)) {
      byRowSelectConditions.push(`\t\t${key}`)
      byRowGroupBy.push(key)
      byRowAllGroupBy.push(key)
    }
    // keep all columns and coalesce unknowns for tags
    for (const col of listOfCols) {
      // CAST first: DuckDB resolves COALESCE(<date>, '<text>') to DATE and casts the
      // literal, so an unquoted column would raise a Conversion Error on NULL instead
      // of falling back to UNKNOWN_VALUE.
      byRowSelectConditions.push(`\t\tCOALESCE(CAST(${col} AS VARCHAR), '${unknownValue}') AS ${col}`)
      byRowGroupBy.push(col)
    }
    // add value aggregations at this row level
    byRowSelectConditions.push(...valuesSelectConditions, ...percentSelectConditions)
    result.push(byRowSelectConditions.join(',\n'))
    result.push('\tFROM base_all')
    result.push('\tGROUP BY ' + byRowGroupBy.join(', '))
    result.push('),')
    //
    lastScope = rowLvl
    if (listOfCols.length === 0) break
    // build nested column levels under the current row scope
    for (let j = listOfCols.length - 1; j >=0; j--) {
      const col = listOfCols[j]
      const colLvl = `${rowLvl.replace('_lvl', `_${prepareName(col)}_lvl`)}`
      result.push(`${colLvl} AS (`)
      result.push('\tSELECT')
      const byColSelectConditions: string[] = []
      const byColGroupBy: string[] = []
      for (const [rowIndex, row] of listOfRows.entries()) {
        byColSelectConditions.push(`\t\t${rowIndex > i ? 'NULL::VARCHAR AS ' : ''}${row}`)
        byColGroupBy.push(row)
      }
      for (const col of listOfCols.slice(0, j)) {
        byColSelectConditions.push(`\t\t${col}`)
        byColGroupBy.push(col)
      }
      // pack values into a map keyed by column values
      const listConditions = [`list(${col})`]
      if (j === listOfCols.length - 1) {
        listConditions.push(`list(${values.length > 0 ? `struct_pack(${values.map(value => `${value} := ${value}`).join(', ')})` : 'true'})`)
      } else {
        listConditions.push(`list(${lastScope}_map)`)
      }
      byColSelectConditions.push(`\t\tmap(\n\t\t\t${listConditions.join(',\n\t\t\t')}\n\t\t) AS ${j === 0 ? MAP_NAME : `${colLvl}_map`}`)
      result.push(byColSelectConditions.join(',\n'))
      result.push(`\tFROM ${lastScope}`)
      result.push('\tGROUP BY ' + byColGroupBy.join(', '))
      result.push('),')
      lastScope = colLvl
    }
    // union all row scopes at the top level
    rowLvls.push(`\n\tSELECT * FROM ${lastScope}`)
  }
  let topScope = 'top_level'
  if (listOfCols.length === 0) {
    topScope = lastScope
  } else {
    // build top-level CTEs for row or column only cases
    if (listOfRows.length > 0) {
      result.push('top_level AS (')
      result.push(rowLvls.join('\n\tUNION ALL'))
      result.push('),')
    } else {
    // no rows: compute grand totals and fold columns into a map
      result.push('grand_total AS (')
      result.push('\tSELECT')
      result.push(valuesSelectConditions)
      result.push('\tFROM base')
      result.push('),')
      const ls = listOfCols.at(0)
      ls && (lastScope = ls)
      result.push(`${lastScope} AS (`)
      result.push('\tSELECT')
      const topScopeSelectConditions: string[] = []
      const topScopeGroupBy: string[] = []
      for (const col of listOfCols) {
        topScopeSelectConditions.push(`\t\tCOALESCE(CAST(${col} AS VARCHAR), '${unknownValue}') AS ${col}`)
        topScopeGroupBy.push(col)
      }
      topScopeSelectConditions.push(...valuesSelectConditions)
      result.push(topScopeSelectConditions.join(',\n'))
      result.push('\tFROM base')
      if (topScopeGroupBy.length) {
        result.push('\tGROUP BY ' + topScopeGroupBy.join(', '))
      }
      result.push('),')
      // roll up columns into nested maps
      for (let j = listOfCols.length - 1; j >=0; j--) {
        const col = listOfCols[j]
        const colLvl = `${prepareName(col)}_lvl`
        result.push(`${colLvl} AS (`)
        result.push('\tSELECT')
        const byColSelectConditions: string[] = []
        const byColGroupBy: string[] = []
        for (const col of listOfCols.slice(0, j)) {
          byColSelectConditions.push(`\t\t${col}`)
          byColGroupBy.push(col)
        }
        // map the column values to either value packs or nested maps
        byColSelectConditions.push(`\t\tmap(list(${col}), ${
          j === listOfCols.length - 1 ? `list(struct_pack(${values.map(value => `${value} := ${value}`).join(', ')}))` : `list(${lastScope}_map)`
        }) AS ${j === 0 ? MAP_NAME : `${colLvl}_map`}`)
        result.push(byColSelectConditions.join(',\n'))
        result.push(`\tFROM ${lastScope}`)
        if (byColGroupBy.length) {
          result.push('\tGROUP BY ' + byColGroupBy.join(', '))
        }
        result.push('),')
        lastScope = colLvl
      }
    }
  }
  result.push('result AS (')
  // final select: project rows/values and serialize column map
  result.push('SELECT')
  if (listOfRows.length === 0) {
    for (const value of values) {
      selectConditions.push(`\tt.${value} AS ${value}`)
    }
  }
  if (listOfCols.length > 0) {
    selectConditions.push(`\tto_json(ty.${MAP_NAME}) AS ${listOfCols.at(0)}`)
  }
  if (existsPercentFields?.length) {
    for (const [fieldName] of existsPercentFields) {
      selectConditions.push(`\tt.${fieldName} AS ${fieldName}`)
    }
  }
  result.push([...selectConditions, ...orderBySelectConditions].join(',\n'))
  result.push(`FROM ${listOfRows.length > 0 ? 'groupped_by_sets t' : `${lastScope} ty`}`)
  // join top level rows to column maps or cross join grand total
  if (listOfRows.length > 0) {
    result.push(`LEFT JOIN ${topScope} ty`)
    const joinConditions: string[] = []
    for (const [index, row] of listOfRows.entries()) {
      joinConditions.push(`ty.${row} ${index !== 0 ? 'IS NOT DISTINCT FROM' : '='} t.${row}`)
    }
    result.push('\tON ' + joinConditions.join(' AND '))
  } else {
    result.push('CROSS JOIN grand_total t')
  }
  // discard ancestor "echo" rows and the redundant grand-total in scoped fetches
  const lastRowFieldFilter = maxRowDepth > 1 ? `t.${listOfRows.at(-1)} IS NOT NULL` : undefined
  const finalWhereParts = [
    whereConditions.length ? `(${whereConditions.join(' OR ')})` : undefined,
    lastRowFieldFilter,
  ].filter(Boolean)
  if (finalWhereParts.length) {
    result.push(`\tWHERE ${finalWhereParts.join(' AND ')}`)
  }
  result.push(')')
  result.push('SELECT')
  result.push('\t(SELECT COUNT(*) FROM result) AS total,')
  result.push('\t(')
  result.push('\t\tSELECT list(t)')
  result.push('\t\tFROM (')
  result.push('\t\t\tSELECT')
  result.push(`\t\t\t\t*${excludeFields.length ? ` EXCLUDE (${excludeFields.join(', ')})` : ''}`)
  result.push('\t\t\tFROM result as t')
  if (orderByConditions.length) {
    result.push(`\t\t\tORDER BY ${orderByConditions.join(',\n\t\t\t\t')}`)
  }
  result.push(`\t\t\tLIMIT ${limit} OFFSET ${offset}`)
  result.push('\t\t) t')
  result.push('\t) as rows')
  // return the full query string
  return result.join('\n')
}

export const getExportData = (
  tableName: string,
  inputFields: PivotTableFields,
  filter?: Filter,
  tagNames?: string[],
  staticFilterConditions?: Record<string, string>,
  percentFields?: PivotTablePercentFields,
  includeCategoryAccount = false,
) => {
  const fields = orderBy(Object.entries(inputFields).filter(([key, field]) => field.enabled && !percentFields?.some(([fieldName]) => fieldName === key)), item => item[1].index)
  const rows = fields?.filter(([, field]) => field.group === PivotTableGroup.ROWS).map(([key]) => key)
  const listOfRows = rows.map(getAnalyticsDuckDbFieldName(tagNames))
  const cols = fields?.filter(([, field]) => field.group === PivotTableGroup.COLS).map(([key]) => key)
  const listOfCols = cols.map(getAnalyticsDuckDbFieldName(tagNames))
  const values = fields?.filter(([, field]) => field.group === PivotTableGroup.VALUES).map(([key]) => key)
  const posibleValues = getExportableValueFields(inputFields, percentFields)
  const listOfDimensions = [...listOfRows, ...listOfCols]
  const exportFields = [...rows, ...cols, ...values]

  if (values.length === 0) return

  const prepareAlias = (field: string) => `"${field.replace(/"/g, '""')}"`
  const unknownValue = UNKNOWN_VALUE.replace(/'/g, '\'\'')
  const selectConditions = listOfDimensions.map((field, index) =>
    `\tCOALESCE(CAST(${field} AS VARCHAR), '${unknownValue}') AS ${prepareAlias(exportFields[index])}`
  )
  const dimensionGroupByConditions = listOfDimensions.map(field => `\t\t${field}`)
  const result = []

  selectConditions.push(...values.map(value => `\tSUM(${value}) AS ${prepareAlias(value)}`))

  result.push(...getAnalyticsTotalQuerySelect(tableName, staticFilterConditions, posibleValues, false, includeCategoryAccount))
  result.push(',')
  result.push(...getAnalyticsBaseQuerySelect(filter, tagNames, posibleValues, [], false, includeCategoryAccount))
  result.push('SELECT')
  result.push(selectConditions.join(',\n'))
  result.push('FROM base')
  if (dimensionGroupByConditions.length) {
    result.push(`GROUP BY ${dimensionGroupByConditions.join(',\n')}`)
    result.push(`ORDER BY ${exportFields.slice(0, listOfDimensions.length).map(prepareAlias).join(', ')}`)
  }
  return {
    queryString: result.join('\n'),
    fields: exportFields,
  }
}
